/**
 * DocumentList Component
 * List of uploaded documents with search functionality
 */

import React from 'react';
import { FiFile, FiTrash2, FiSearch, FiFileText, FiLoader } from 'react-icons/fi';
import type { KnowledgeDocument, KnowledgeSearchResult } from '../../types';

interface DocumentListProps {
  documents: KnowledgeDocument[];
  selectedDocument: KnowledgeDocument | null;
  onSelect: (document: KnowledgeDocument | null) => void;
  onDelete: (documentId: string) => void;
  onSearch: (query: string) => void;
  searchQuery: string;
  searchResults: KnowledgeSearchResult[];
  isSearching: boolean;
  className?: string;
}

export const DocumentList: React.FC<DocumentListProps> = ({
  documents,
  selectedDocument,
  onSelect,
  onDelete,
  onSearch,
  searchQuery,
  searchResults,
  isSearching,
  className = ''
}) => {
  const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const formatDate = (date: Date): string => {
    return new Date(date).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const displayList = searchQuery.trim() && searchResults.length > 0
    ? searchResults.map(r => r.document).filter((doc): doc is KnowledgeDocument => doc !== undefined)
    : documents;

  return (
    <div className={`bg-slate-800/50 backdrop-blur-sm rounded-xl border border-slate-700 flex flex-col ${className}`}>
      {/* Header */}
      <div className="p-4 border-b border-slate-700">
        <h3 className="text-lg font-semibold text-white mb-3">Knowledge Base</h3>

        {/* Search Bar */}
        <div className="relative">
          <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={16} />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => onSearch(e.target.value)}
            placeholder="Search documents..."
            className="w-full pl-10 pr-4 py-2 bg-slate-900 border border-slate-700 rounded-lg text-gray-300 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
          {isSearching && (
            <FiLoader className="absolute right-3 top-1/2 -translate-y-1/2 text-blue-400 animate-spin" size={16} />
          )}
        </div>
      </div>

      {/* Document List */}
      <div className="flex-1 overflow-y-auto max-h-[500px]">
        {displayList.length === 0 ? (
          <div className="p-8 text-center">
            {searchQuery.trim() ? (
              <>
                <FiSearch className="mx-auto text-gray-500 mb-3" size={48} />
                <p className="text-gray-400">No documents found</p>
                <p className="text-sm text-gray-500 mt-1">Try a different search term</p>
              </>
            ) : (
              <>
                <FiFileText className="mx-auto text-gray-500 mb-3" size={48} />
                <p className="text-gray-400">No documents uploaded</p>
                <p className="text-sm text-gray-500 mt-1">Upload documents to build your knowledge base</p>
              </>
            )}
          </div>
        ) : (
          <div className="p-2 space-y-1">
            {displayList.map((doc) => {
              const isSelected = selectedDocument?.id === doc.id;

              return (
                <div
                  key={doc.id}
                  className={`
                    group relative rounded-lg border transition-all cursor-pointer
                    ${isSelected
                      ? 'border-blue-500 bg-blue-500/10'
                      : 'border-slate-700 bg-slate-900/50 hover:border-slate-600 hover:bg-slate-800/50'
                    }
                  `}
                  onClick={() => onSelect(doc)}
                >
                  <div className="p-3">
                    {/* Document Icon & Name */}
                    <div className="flex items-start gap-3">
                      <FiFile className={isSelected ? 'text-blue-400' : 'text-gray-400'} size={20} />
                      <div className="flex-1 min-w-0">
                        <h4 className="text-sm font-medium text-white truncate">
                          {doc.title || doc.filename}
                        </h4>
                        <div className="flex items-center gap-2 text-xs text-gray-500 mt-1">
                          <span>{formatFileSize(doc.size)}</span>
                          <span>•</span>
                          <span>{doc.uploadedAt && formatDate(new Date(doc.uploadedAt))}</span>
                        </div>
                        {doc.status && (
                          <div className="mt-2">
                            <span className={`
                              inline-block px-2 py-1 text-xs rounded
                              ${doc.status === 'completed' ? 'bg-green-500/20 text-green-400' : ''}
                              ${doc.status === 'processing' ? 'bg-yellow-500/20 text-yellow-400' : ''}
                              ${doc.status === 'failed' ? 'bg-red-500/20 text-red-400' : ''}
                            `}>
                              {doc.status}
                            </span>
                          </div>
                        )}
                      </div>

                      {/* Delete Button */}
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          if (window.confirm(`Delete "${doc.title || doc.filename}"?`)) {
                            onDelete(doc.id);
                          }
                        }}
                        className="opacity-0 group-hover:opacity-100 p-2 hover:bg-red-500/20 rounded transition-all"
                        title="Delete document"
                      >
                        <FiTrash2 className="text-red-400" size={16} />
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Footer Stats */}
      <div className="p-3 border-t border-slate-700 bg-slate-900/50">
        <p className="text-xs text-gray-400">
          {searchQuery.trim() && searchResults.length > 0
            ? `${searchResults.length} search results`
            : `${documents.length} documents`
          }
        </p>
      </div>
    </div>
  );
};

export default DocumentList;
